import {z} from 'zod'

export const userRoleSchema = z.enum(['admin', 'manager', 'member'])

export type UserRole = z.infer<typeof userRoleSchema>

export const USER_ROLE_LABELS: Record<UserRole, string> = {
    admin: 'Admin',
    manager: 'Manager',
    member: 'Member',
}

export const userAccountRawSchema = z.object({
    id: z.string(),
    email: z.string(),
    name: z.string(),
    role: userRoleSchema,
    is_sales_rep: z.boolean().optional(),
    dispatch_yard_id: z.string().nullable().optional(),
    created_at: z.string(),
    deactivated_at: z.string().nullable(),
})

export const userAccountSchema = userAccountRawSchema.transform((raw) => ({
    id: raw.id,
    email: raw.email,
    name: raw.name,
    role: raw.role,
    isSalesRep: raw.is_sales_rep ?? false,
    /** Yard the user dispatches or works for; null for users across both yards. */
    dispatchYardId: raw.dispatch_yard_id ?? null,
    createdAt: raw.created_at,
    deactivatedAt: raw.deactivated_at,
}))

export type UserAccountRaw = z.infer<typeof userAccountRawSchema>
export type UserAccount = z.infer<typeof userAccountSchema>

export const createUserInputSchema = z.object({
    email: z.string().trim().email(),
    name: z.string().trim().min(1),
    role: userRoleSchema.default('member'),
    isSalesRep: z.boolean().default(false),
    dispatchYardId: z.string().nullable().default(null),
})

export type CreateUserInput = z.input<typeof createUserInputSchema>

export const createUserRequestSchema = createUserInputSchema.transform(
    ({email, name, role, isSalesRep, dispatchYardId}) => ({
        email: email.trim().toLowerCase(),
        name,
        role: role ?? 'member',
        is_sales_rep: isSalesRep ?? false,
        dispatch_yard_id: dispatchYardId ?? null,
    }),
)

export type CreateUserRequestRaw = z.infer<typeof createUserRequestSchema>

export const updateUserInputSchema = z.object({
    name: z.string().trim().min(1).optional(),
    role: userRoleSchema.optional(),
    isSalesRep: z.boolean().optional(),
    active: z.boolean().optional(),
    dispatchYardId: z.string().nullable().optional(),
})

export type UpdateUserInput = z.input<typeof updateUserInputSchema>

export const updateUserRequestSchema = updateUserInputSchema.transform((input) => ({
    ...(input.name !== undefined ? {name: input.name} : {}),
    ...(input.role !== undefined ? {role: input.role} : {}),
    ...(input.isSalesRep !== undefined ? {is_sales_rep: input.isSalesRep} : {}),
    ...(input.active !== undefined ? {active: input.active} : {}),
    ...(input.dispatchYardId !== undefined ? {dispatch_yard_id: input.dispatchYardId} : {}),
}))

export type UpdateUserRequestRaw = z.infer<typeof updateUserRequestSchema>

export const projectContactRawSchema = z.object({
    id: z.string(),
    name: z.string(),
    email: z.string(),
    is_sales_rep: z.boolean(),
})

export const projectContactSchema = projectContactRawSchema.transform((raw) => ({
    id: raw.id,
    name: raw.name,
    email: raw.email,
    isSalesRep: raw.is_sales_rep,
}))

export type ProjectContactRaw = z.infer<typeof projectContactRawSchema>
export type ProjectContact = z.infer<typeof projectContactSchema>
